import type { PlaceResult } from "@/lib/api/misc";
import { searchPlacesResult } from "@/lib/api/misc";

/**
 * Submit-time typed-city resolver — the birthPlace conflation fix.
 *
 * Before this, the submit handler called `searchPlaces` (which swallows network
 * errors into `[]`) and returned `null` on anything that wasn't a confident
 * match. A misspelled city, a genuinely ambiguous one, and a flaky-network
 * lookup ALL collapsed to the same `null` → the same "pick from the dropdown"
 * error. That single conflation is the documented root of the 15,124 birthPlace
 * failures: we could never tell ambiguity from a true network fault.
 *
 * This resolver returns a DISCRIMINATED outcome so the caller maps each to the
 * right UX (rule 39: one obvious correction path):
 *   - resolved      → commit the chip and submit (a confident typed match)
 *   - ambiguous     → show a "did you mean" chip of the top suggestion
 *   - empty         → field genuinely blank / no results → "pick your city"
 *   - network_error → render the retry card; this is NOT a bad-city error
 *
 * The confidence rule (unchanged from the prior inline `resolveTypedPlace`):
 * the top result must EXACTLY match (case-insensitive) what the user typed, OR
 * the user typed ≥4 chars and the top result's name prefix-matches, OR a single
 * result for a ≥4-char query (unambiguous).
 */
export type TypedPlaceResolution =
  | { kind: "resolved"; place: PlaceResult }
  | { kind: "ambiguous"; suggestion: PlaceResult }
  | { kind: "empty" }
  | { kind: "network_error"; cause: string };

export async function resolveTypedPlace(
  query: string,
  search: typeof searchPlacesResult = searchPlacesResult
): Promise<TypedPlaceResolution> {
  const q = query.trim();
  if (!q) return { kind: "empty" };

  const res = await search(q, 5);
  if (res.status === "network_error") {
    return { kind: "network_error", cause: res.cause };
  }

  const results = res.results;
  if (!results.length) return { kind: "empty" };

  const top = results[0];
  const ql = q.toLowerCase();
  const exact = top.display.toLowerCase() === ql || top.name.toLowerCase() === ql;
  const prefix = q.length >= 4 && top.name.toLowerCase().startsWith(ql);
  const unambiguous = q.length >= 4 && results.length === 1;
  if (exact || prefix || unambiguous) return { kind: "resolved", place: top };

  // Multiple plausible matches and none confidently the user's → surface the
  // top as a tappable "did you mean" chip instead of hard-rejecting.
  return { kind: "ambiguous", suggestion: top };
}
